import { computed, effect, inject, Injectable, Signal, WritableSignal } from '@angular/core';
import { combineLatest, map, Observable } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { B2BLegalEntityStore } from '../store/b2b-legal-entity-store';
import { LEGAL_ENTITY_DATA } from '@lentau/profile/organizations/legal-entity/shared/tokens/legal-entity-data.token';
import { LegalEntityResponseManagerData } from 'angular-api-gateway/v1/model/legalEntityResponseManagerData';
import { LegalEntityResponse } from 'angular-api-gateway/v1/model/legalEntityResponse';
import { LegalEntityData } from 'angular-api-gateway/v1/model/legalEntityData';

@Injectable({
  providedIn: 'root',
})
export class B2BLegalEntityFacade {
  private store: InstanceType<typeof B2BLegalEntityStore> = inject(B2BLegalEntityStore);
  public legalEntityData: Signal<LegalEntityData> = this.store.legalEntity;
  public legalEntityData$: Observable<LegalEntityData> = toObservable(this.legalEntityData);
  public orgName: Signal<string> = this.store.name;
  public email: Signal<string> = this.store.email;
  public phone: Signal<string> = this.store.phone;
  public regDate: Signal<number> = this.store.regDate;
  public account: Signal<string> = this.store.account;
  private ONE_MONTH_TIMESTAMP: number = 30 * 24 * 60 * 60 * 1000;
  private legalEntityDataToken: WritableSignal<LegalEntityResponse> = inject(LEGAL_ENTITY_DATA);

  constructor() {
    this.initializeLegalEntityDataToken();
  }

  public b2bProManager: Signal<LegalEntityResponseManagerData> = this.store.managerData;
  public isEdoEnabled: Signal<boolean> = this.store.edo;

  public loading: Signal<boolean> = this.store.loading;
  public loading$: Observable<boolean> = toObservable(this.loading);
  public alcoOwnNeedsPostSuccessCount: Signal<number> = this.store.alcoOwnNeedsPostSuccessCount;
  public alcoOwnNeedsPostErrorCount: Signal<number> = this.store.alcoOwnNeedsPostErrorCount;
  public alcoOwnNeedsPostErrorMessage: Signal<string> = this.store.alcoOwnNeedsPostErrorMessage;

  public isNewUser: Signal<boolean> = computed(() => {
    return Date.now() - this.regDate() * 1000 <= this.ONE_MONTH_TIMESTAMP;
  });

  public b2bLoadedAndValid$: Observable<boolean> = combineLatest([this.legalEntityData$, this.loading$]).pipe(
    map(([proData, proDataLoading]: [LegalEntityResponse, boolean]) => !!proData.siebelId || proDataLoading)
  );
  public b2bLoadedAndValid: Signal<boolean> = toSignal(this.b2bLoadedAndValid$);

  public selectCardPro: Signal<string> = this.store.proCardNumber;
  public selectCardPro$: Observable<string> = toObservable(this.selectCardPro);

  public getLegalEntity(): void {
    this.store.getLegalEntity();
  }

  public postLegalEntityAlcoOwnNeeds(): void {
    this.store.postLegalEntityAlcoOwnNeeds();
  }

  private initializeLegalEntityDataToken(): void {
    effect((): void => this.legalEntityDataToken.set(this.legalEntityData()));
  }
}
